import { enqueueSnackbar } from '~/utils/notifications';
import { useCallback } from 'react';
import { useIntl } from 'react-intl';
import { useRecoilState, useRecoilValue } from 'recoil';
import { ContourTexture } from '~/engine/data/structure/ContourTexture';
import { RegionOfInterest } from '~/engine/data/structure/RegionOfInterest';
import { GlOperationGuard } from '~/engine/util/algorithms/sdf/utils';
import { VolumeViewUpdateEvent } from '~/engine/view/events/VolumeViewUpdateEvent';
import { LayerBit } from '~/engine/view/layers/LayerBit';
import { ViewManager } from '~/engine/view/ViewManager';
import useBusyIndicator from '~/hooks/useBusyIndicator';
import { environmentState } from '~/state/atoms/engineAtoms';
import {
    sdfOperationInFlightState,
    selectedROIState,
} from '~/state/atoms/structureSet/roiAtoms';

interface OperationResult {
    texture: ContourTexture;
    successValues?: Record<string, string | number>;
}

interface ExecuteOperationParams {
    /** Async function that performs the actual SDF operation. */
    operationFn: (
        roi: RegionOfInterest
    ) => Promise<OperationResult | undefined>;
    /** Label stored in the ROI's undo history entry. */
    operationName: string;
    /** intl message ID for the busy indicator overlay. */
    busyMessageId: string;
    /** intl message ID for the success snackbar. */
    successMessageId: string;
    /** intl message ID for the failure snackbar. */
    failedMessageId: string;
    /** Label for console.error logging. */
    logLabel: string;
}

/**
 * Hook that extracts the shared handler lifecycle for ROI SDF
 * operations (smoothing, remove small parts, fill cavities, etc.):
 * validation → busy indicator → try/catch with replaceTexture
 * + ViewManager update + snackbars → finally cleanup.
 */
export function useRoiSdfOperation() {
    const intl = useIntl();
    const environment = useRecoilValue(environmentState);
    const selectedROI = useRecoilValue(selectedROIState);
    const { showBusy, hideBusy } = useBusyIndicator();
    const [isProcessing, setIsProcessing] = useRecoilState(
        sdfOperationInFlightState
    );

    const getRoiAndValidate = useCallback(() => {
        const structureSet = environment?.structureSet;
        if (!structureSet) {
            enqueueSnackbar(
                intl.formatMessage({
                    id: 'postProcessing.noStructureSet',
                }),
                { variant: 'error' }
            );
            return null;
        }

        const roi = structureSet.getRoi(selectedROI);
        if (!roi) {
            enqueueSnackbar(
                intl.formatMessage({
                    id: 'postProcessing.structureNotFound',
                }),
                { variant: 'error' }
            );
            return null;
        }

        if (!roi.texture) {
            enqueueSnackbar(
                intl.formatMessage({
                    id: 'postProcessing.noTextureData',
                }),
                { variant: 'error' }
            );
            return null;
        }

        if (!roi.canModify) {
            enqueueSnackbar(
                intl.formatMessage({
                    id: 'structureCannotBeModified',
                }),
                { variant: 'error' }
            );
            return null;
        }

        return { structureSet, roi };
    }, [environment, selectedROI, intl]);

    const executeOperation = useCallback(
        async (params: ExecuteOperationParams) => {
            // GlOperationGuard.isActive is a synchronous check that
            // prevents double-click races (Recoil state is async).
            if (isProcessing || GlOperationGuard.isActive) {
                return;
            }

            const validation = getRoiAndValidate();
            if (!validation) {
                return;
            }
            const { structureSet, roi } = validation;

            setIsProcessing(true);
            await showBusy(
                intl.formatMessage(
                    { id: params.busyMessageId },
                    { name: roi.name }
                )
            );

            try {
                const result = await params.operationFn(roi);

                if (result) {
                    roi.replaceTexture(result.texture, params.operationName);

                    ViewManager.viewUpdateEvent(
                        new VolumeViewUpdateEvent(
                            structureSet,
                            LayerBit.CONTOUR_LAYER_BIT,
                            true
                        )
                    );

                    enqueueSnackbar(
                        intl.formatMessage(
                            { id: params.successMessageId },
                            {
                                name: roi.name,
                                ...(result.successValues ?? {}),
                            }
                        ),
                        { variant: 'success' }
                    );
                } else {
                    enqueueSnackbar(
                        intl.formatMessage({
                            id: params.failedMessageId,
                        }),
                        { variant: 'error' }
                    );
                }
            } catch (error) {
                console.error(`${params.logLabel} error:`, error);
                enqueueSnackbar(
                    intl.formatMessage({
                        id: params.failedMessageId,
                    }),
                    { variant: 'error' }
                );
            } finally {
                setIsProcessing(false);
                hideBusy();
            }
        },
        [
            getRoiAndValidate,
            showBusy,
            hideBusy,
            intl,
            isProcessing,
            setIsProcessing,
        ]
    );

    return { isProcessing, executeOperation };
}
